import { apiClient } from './client';
import type { ClientsPageMeta } from './clients';
import type { AdMedia, UpdateMediaPayload } from './ads';

/**
 * Баннер (вкладка "Баннеры" раздела «Маркетинг») — /banners (см. докблок
 * BannerController в Laravel, 1:1 старые Orchid-экраны Marketing*Screen).
 * Медиафайл — та же форма, что у объявлений (общее хранилище вложений).
 */
export type BannerMedia = AdMedia;

/** Правило показа (в Orchid — «сегмент», таблица banners_segments). */
export type BannerSegmentType = 'all' | 'test' | 'active';

export interface BannerSegment {
  type: BannerSegmentType;
  /** MaintestName выбранного теста — только у type='test'. */
  data: string | null;
  /** Название теста для показа (сервер резолвит код в TestTitle); при сохранении не требуется. */
  dataLabel?: string | null;
  /** Границы активности пользователя — только у type='active'. */
  dateFrom: string | null;
  dateTo: string | null;
}

export interface BannerRow {
  id: number;
  name: string;
  hint: string | null;
  description: string | null;
  url: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  active: boolean;
  media: BannerMedia | null;
  segments: BannerSegment[];
}

export interface FetchBannersParams {
  search?: string;
  page?: number;
  /** '1' | '0' — фильтр по статусу показа; undefined — все. */
  active?: string;
}

export interface BannersPage {
  items: BannerRow[];
  meta: ClientsPageMeta;
}

export async function fetchBanners(params: FetchBannersParams = {}): Promise<BannersPage> {
  const { data } = await apiClient.get<BannersPage>('/banners', { params });
  return data;
}

export interface SaveBannerPayload {
  name: string;
  hint?: string | null;
  description?: string | null;
  url?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  active: boolean;
  mediaId?: number | null;
  segments?: BannerSegment[];
}

export async function createBanner(payload: SaveBannerPayload): Promise<BannerRow> {
  const { data } = await apiClient.post<BannerRow>('/banners', payload);
  return data;
}

export async function updateBanner(id: number, payload: SaveBannerPayload): Promise<BannerRow> {
  const { data } = await apiClient.put<BannerRow>(`/banners/${id}`, payload);
  return data;
}

export async function deleteBanner(id: number): Promise<void> {
  await apiClient.delete(`/banners/${id}`);
}

export async function uploadBannerMedia(file: File): Promise<BannerMedia> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<BannerMedia>('/banners/media', form);
  return data;
}

export async function updateBannerMedia(
  mediaId: number,
  payload: UpdateMediaPayload,
): Promise<BannerMedia> {
  const { data } = await apiClient.put<BannerMedia>(`/banners/media/${mediaId}`, payload);
  return data;
}

export interface SegmentTestOption {
  value: string;
  label: string;
}

export async function fetchSegmentTests(): Promise<SegmentTestOption[]> {
  const { data } = await apiClient.get<{ tests: SegmentTestOption[] }>('/banners/segment-tests');
  return data.tests;
}

/** Включение/отключение из списка — отдельный маршрут, без валидации всей записи. */
export async function setBannerActive(id: number, active: boolean): Promise<BannerRow> {
  const { data } = await apiClient.put<BannerRow>(`/banners/${id}/active`, { active });
  return data;
}
